var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Image } from 'src/ui/components/Image/Image';
import { ErrorPlaceholder } from 'src/ui/views/dynamics/CaseGlobalViewPage/components/FilesViewer/placeholders/ErrorPlaceholder';
import { Icons } from 'src/ui/components/Icons/Icons';
import { FileViewerActions } from 'src/ui/views/dynamics/CaseGlobalViewPage/components/FilesViewer/FileViewerActions';
import { useEffectWithPrevious, useMount, useUpdate } from 'src/shared/hooks/useLifecycle';
import { calculateImagePositionWithMouseCoord } from 'src/ui/views/dynamics/CaseGlobalViewPage/components/FilesViewer/helpers/helpers';
import { windowExist } from 'src/shared/constants';
import { E_ICONS } from 'src/ui/components/Icons/constants';
import styles from './fileViewer.module.scss';
import { getVideoSourceType, Video } from 'src/ui/components/VideoPlayer/components/Video/Video';
import { useFileViewerKeyboardListener } from './fileViewerKeyboardListener';
import { getPropsByKeyPath } from 'src/shared/helpers/objHelpers';
import { rotateBase64 } from 'src/shared/helpers/viewsHelpers';
import { E_FILE_TYPE } from 'src/redux/core/types';
import deepEquals from 'fast-deep-equal';
export var FileViewer = memo(function (props) {
    var _a;
    var i18n = props.i18n, currentDocument = props.currentDocument, _b = props.scaleOnMouseHover, scaleOnMouseHoverProps = _b === void 0 ? 2 : _b, _c = props.maxScale, maxScale = _c === void 0 ? 8 : _c, _d = props.minScale, minScale = _d === void 0 ? 2 : _d, _e = props.scaleIncrement, scaleIncrement = _e === void 0 ? 1 : _e, sources = props.sources, currentPage = props.currentPage, disabledShortcuts = props.disabledShortcuts, isOnFullScreen = props.isOnFullScreen, onPageChange = props.onPageChange, onPageClick = props.onPageClick, onFullScreenViewer = props.onFullScreenViewer, onDownloadFileClick = props.onDownloadFileClick, onSourcesChange = props.onSourcesChange;
    var _f = useState(false), pageError = _f[0], setPageError = _f[1];
    var _g = useState(null), dynamicStyle = _g[0], setDynamicStyle = _g[1];
    var _h = useState(null), heightOfContainer = _h[0], setHeightOfContainer = _h[1];
    var _j = useState(null), hoverPos = _j[0], setHoverPos = _j[1];
    var _k = useState(scaleOnMouseHoverProps), scaleOnMouseHover = _k[0], setScaleOnMouseHover = _k[1];
    var containerRef = useRef(null);
    var actionsRef = useRef(null);
    var isFirstPage = currentPage === 0;
    var isLastPage = currentPage === sources.length - 1;
    var isScrollListenerEnabled = useRef(true);
    var timeoutEnableScrollListener = null;
    var timeoutEndScroll = null;
    useMount(function () {
        return function () {
            clearTimeout(timeoutEnableScrollListener);
            clearTimeout(timeoutEndScroll);
        };
    });
    var handleResizeWindow = function () {
        var _a, _b, _c;
        if ((_a = containerRef === null || containerRef === void 0 ? void 0 : containerRef.current) === null || _a === void 0 ? void 0 : _a.offsetHeight) {
            if ((_b = actionsRef === null || actionsRef === void 0 ? void 0 : actionsRef.current) === null || _b === void 0 ? void 0 : _b.offsetHeight) {
                setHeightOfContainer(containerRef.current.offsetHeight - ((_c = actionsRef === null || actionsRef === void 0 ? void 0 : actionsRef.current) === null || _c === void 0 ? void 0 : _c.offsetHeight));
            }
            else {
                // 75 is taller than actions box
                setHeightOfContainer(containerRef.current.offsetHeight - 75);
            }
        }
    };
    useUpdate(function () {
        setScaleOnMouseHover(scaleOnMouseHoverProps);
    }, [currentPage, sources]);
    useEffect(function () {
        setPageError(false);
    }, [sources]);
    // If sources change between two renders, meaning the user change document so we don't want to show scroll animation
    useEffectWithPrevious(function (_a) {
        var prevSources = _a[0];
        if (containerRef.current) {
            clearTimeout(timeoutEnableScrollListener);
            isScrollListenerEnabled.current = false;
            var isSameSource = deepEquals(prevSources, sources);
            if (isSameSource) {
                scrollToPage(currentPage, true);
            }
            else {
                scrollToPage(currentPage, true, 'instant');
            }
        }
    }, [sources, currentPage, containerRef.current]);
    useUpdate(function () {
        handleResizeWindow();
        if (windowExist) {
            window.addEventListener('resize', handleResizeWindow);
            return function () { return window.removeEventListener('resize', handleResizeWindow); };
        }
    }, [containerRef.current]);
    var scrollToPage = function (page, enableScrollListener, behavior) {
        if (enableScrollListener === void 0) { enableScrollListener = false; }
        if (behavior === void 0) { behavior = 'smooth'; }
        if (containerRef === null || containerRef === void 0 ? void 0 : containerRef.current) {
            var offsetWidth = containerRef.current.offsetWidth;
            var paddingToSubtract = 48;
            var minScrollAuthorized = Math.max(page * offsetWidth - paddingToSubtract, 0);
            containerRef.current.scrollTo({ left: minScrollAuthorized, behavior: behavior });
            if (enableScrollListener) {
                // Prevents scroll listener to be enabled too early, could lead to bug if user uses arrow followed by scroll within 500ms
                timeoutEnableScrollListener = setTimeout(function () {
                    isScrollListenerEnabled.current = true;
                }, 500);
            }
        }
    };
    var handleRotateRight = function () {
        var imageToUpdate = sources[currentPage];
        rotateBase64(imageToUpdate.src, true).then(function (base64) {
            var sourceRotated = __assign(__assign({}, imageToUpdate), { src: base64 || '' });
            onSourcesChange(sourceRotated);
        });
    };
    var handleRotateLeft = function () {
        var imageToUpdate = sources[currentPage];
        rotateBase64(imageToUpdate.src, false).then(function (base64) {
            var sourceRotated = __assign(__assign({}, imageToUpdate), { src: base64 || '' });
            onSourcesChange(sourceRotated);
        });
    };
    var handlePreviousPage = function () {
        if (!isFirstPage) {
            scrollToPage(currentPage - 1);
        }
    };
    var handleNextPage = function () {
        if (!isLastPage) {
            scrollToPage(currentPage + 1);
        }
    };
    var handleSave = function () {
        onDownloadFileClick(currentDocument.files[currentPage].id);
    };
    //onScrollEnd don't work on safari
    var handleFilesEndScroll = function (e) {
        clearTimeout(timeoutEndScroll);
        if ((containerRef === null || containerRef === void 0 ? void 0 : containerRef.current) && !isOnFullScreen) {
            e.preventDefault();
            timeoutEndScroll = setTimeout(function () {
                var _a = e.target, scrollLeft = _a.scrollLeft, clientWidth = _a.clientWidth;
                var newPage = Math.ceil(scrollLeft / clientWidth);
                if (onPageChange) {
                    onPageChange(newPage);
                }
            }, 100);
        }
    };
    var handleMouseHover = function (e) {
        e.stopPropagation();
        var objectPosition = calculateImagePositionWithMouseCoord({ width: e.target.offsetWidth, height: e.target.offsetHeight }, { xMouse: e.nativeEvent.offsetX, yMouse: e.nativeEvent.offsetY }, scaleOnMouseHover);
        if (!hoverPos) {
            // must update mouse position at first time
            setHoverPos({ x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY });
        }
        else {
            var xDifference = hoverPos.x - e.nativeEvent.offsetX;
            var yDifference = hoverPos.y - e.nativeEvent.offsetY;
            var aliasing = 0.5; // prevent multiple update for same mouse position
            if (xDifference > aliasing || xDifference < -aliasing || yDifference > aliasing || yDifference < -aliasing) {
                // must update mouse position for next movement event
                setHoverPos({ x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY });
                setDynamicStyle({ objectPosition: objectPosition, transform: "scale(".concat(scaleOnMouseHover, ")") });
            }
        }
    };
    var handleMouseLeave = function () { return setDynamicStyle(null); };
    var isMaxScale = scaleOnMouseHover >= maxScale;
    var isMinScale = scaleOnMouseHover <= minScale;
    var handleZoomInClick = function () {
        var newScale = Math.min(scaleOnMouseHover + scaleIncrement, maxScale);
        setScaleOnMouseHover(newScale);
    };
    var handleZoomOutClick = function () {
        var newScale = Math.max(scaleOnMouseHover - scaleIncrement, minScale);
        setScaleOnMouseHover(newScale);
    };
    var arrowLeftClassName = classNames(styles.fileViewerArrowLeft);
    var arrowRightClassName = classNames(styles.fileViewerArrowRight);
    var videoIsShown = ((_a = sources[currentPage]) === null || _a === void 0 ? void 0 : _a.type) === E_FILE_TYPE.VIDEO;
    useFileViewerKeyboardListener(handlePreviousPage, handleNextPage, disabledShortcuts);
    var errorMessage = getPropsByKeyPath(i18n.modules, 'documentViewer.errorMessage');
    var zoomInTooltip = getPropsByKeyPath(i18n.components, 'tooltips.video.zoomIn');
    var zoomOutTooltip = getPropsByKeyPath(i18n.components, 'tooltips.video.zoomOut');
    var handlePageClick = function (source) {
        if (onPageClick && onFullScreenViewer) {
            if (source.type === E_FILE_TYPE.IMAGE) {
                onFullScreenViewer();
                onPageClick(currentPage);
            }
        }
    };
    var handleOnFullScreenViewer = function () {
        onFullScreenViewer();
    };
    return (_jsxs("section", __assign({ className: classNames(styles.fileViewerWrapper, 'file-viewer') }, { children: [_jsx("div", __assign({ style: { gridTemplateColumns: "repeat(".concat((currentDocument === null || currentDocument === void 0 ? void 0 : currentDocument.files.length) || 0, ", 100%)") }, className: styles.fileViewer, ref: containerRef, onScroll: handleFilesEndScroll }, { children: sources.map(function (source, index) { return (_jsx("div", __assign({ onClick: function () { return handlePageClick(source); } }, { children: source.type === 'image' ? (_jsx(Image, { isLoading: source.isLoading, src: source.isError ? '' : source.src, alt: 'file', className: styles.image, style: __assign({ maxHeight: heightOfContainer }, (index === currentPage ? dynamicStyle : null)), onMouseMove: handleMouseHover, onMouseLeave: handleMouseLeave, onLoad: function () { return (index === currentPage ? setPageError(false) : null); }, onError: function (e) { return (index === currentPage ? setPageError(true) : null); }, placeholderOnError: _jsx(ErrorPlaceholder, { title: errorMessage, text: source.name }) })) : (_jsx(Video, { style: { maxHeight: heightOfContainer }, sources: [
                            { src: source.src, type: source.mimeType || getVideoSourceType(source.src) },
                        ], withTimeValidation: false, zoomInTooltip: zoomInTooltip, zoomOutTooltip: zoomOutTooltip })) }), source.id)); }) })), _jsx(FileViewerActions, { i18n: i18n, ref: actionsRef, disabled: pageError, hidden: videoIsShown, onRotateLeft: handleRotateLeft, onRotateRight: handleRotateRight, onDownload: onDownloadFileClick ? handleSave : null, onFullScreenViewer: onFullScreenViewer ? handleOnFullScreenViewer : null, onZoomIn: isMaxScale ? null : handleZoomInClick, onZoomOut: isMinScale ? null : handleZoomOutClick }), !isFirstPage && (_jsx("div", __assign({ className: arrowLeftClassName }, { children: _jsx(Icons, { icon: E_ICONS.ARROW_LEFT, className: styles.fileViewerArrowImg, onClick: handlePreviousPage }) }))), !isLastPage && (_jsx("div", __assign({ className: arrowRightClassName }, { children: _jsx(Icons, { icon: E_ICONS.ARROW_RIGHT, className: styles.fileViewerArrowImg, onClick: handleNextPage }) })))] })));
});
